import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { Mail, Reply, Trash2, Eye, Archive, Search, Filter } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminMessages = () => {
  const { getAuthHeaders } = useAuth();
  const [messages, setMessages] = useState([]);
  const [stats, setStats] = useState({ total: 0, unread: 0, replied: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const fetchMessages = async () => {
    try {
      const [messagesRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/admin/messages`, getAuthHeaders()),
        axios.get(`${API_URL}/admin/messages/stats`, getAuthHeaders())
      ]);
      setMessages(messagesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
  }, []);

  const handleReply = async (messageId) => {
    if (!replyText.trim()) {
      toast.error('Please enter a reply');
      return;
    }
    try {
      await axios.put(`${API_URL}/admin/messages/${messageId}/reply`, 
        { reply: replyText }, 
        getAuthHeaders()
      );
      toast.success('Reply sent successfully');
      setReplyText('');
      setSelectedMessage(null);
      fetchMessages();
    } catch (error) {
      toast.error('Failed to send reply');
    }
  };

  const handleStatusChange = async (messageId, status) => {
    try {
      await axios.put(`${API_URL}/admin/messages/${messageId}/status?status=${status}`, {}, getAuthHeaders());
      toast.success('Status updated');
      fetchMessages();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (messageId) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;
    try {
      await axios.delete(`${API_URL}/admin/messages/${messageId}`, getAuthHeaders());
      toast.success('Message deleted');
      setSelectedMessage(null);
      fetchMessages();
    } catch (error) {
      toast.error('Failed to delete message');
    }
  };

  const filteredMessages = messages.filter(msg => {
    const matchesFilter = filter === 'all' || msg.status === filter;
    const matchesSearch = msg.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      msg.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      msg.subject.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const getStatusBadge = (status) => {
    const styles = {
      unread: 'bg-red-100 text-red-700',
      read: 'bg-blue-100 text-blue-700',
      replied: 'bg-green-100 text-green-700',
      archived: 'bg-gray-100 text-gray-700'
    };
    return <Badge className={styles[status]}>{status}</Badge>;
  };

  const getTypeBadge = (type) => {
    const styles = {
      general: 'bg-gray-100 text-gray-700',
      inquiry: 'bg-blue-100 text-blue-700',
      feedback: 'bg-purple-100 text-purple-700',
      support: 'bg-orange-100 text-orange-700',
      collaboration: 'bg-green-100 text-green-700'
    };
    return <Badge className={styles[type] || styles.general}>{type}</Badge>;
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Messages</h1>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
            <p className="text-gray-500 text-sm">Total Messages</p>
          </CardContent>
        </Card>
        <Card className="border-red-200">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-red-600">{stats.unread}</p>
            <p className="text-gray-500 text-sm">Unread</p>
          </CardContent>
        </Card>
        <Card className="border-green-200">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-green-600">{stats.replied}</p>
            <p className="text-gray-500 text-sm">Replied</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search by name, email, or subject..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex gap-2">
          {['all', 'unread', 'read', 'replied', 'archived'].map(f => (
            <Button
              key={f}
              variant={filter === f ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter(f)}
              className={filter === f ? 'bg-saffron-500' : ''}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </Button>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Messages List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Inbox ({filteredMessages.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 max-h-[600px] overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center text-gray-500">Loading...</div>
            ) : filteredMessages.length === 0 ? (
              <div className="p-4 text-center text-gray-500">No messages found</div>
            ) : (
              <div className="divide-y">
                {filteredMessages.map(msg => (
                  <div
                    key={msg.id}
                    className={`p-4 cursor-pointer hover:bg-gray-50 ${selectedMessage?.id === msg.id ? 'bg-saffron-50' : ''} ${msg.status === 'unread' ? 'bg-blue-50/50' : ''}`}
                    onClick={() => {
                      setSelectedMessage(msg);
                      if (msg.status === 'unread') handleStatusChange(msg.id, 'read');
                    }}
                  >
                    <div className="flex justify-between items-start mb-1">
                      <span className="font-medium text-gray-900">{msg.name}</span>
                      {getStatusBadge(msg.status)}
                    </div>
                    <p className="text-sm text-gray-600 truncate">{msg.subject}</p>
                    <div className="flex justify-between items-center mt-2">
                      <span className="text-xs text-gray-400">{new Date(msg.created_at).toLocaleDateString()}</span>
                      {getTypeBadge(msg.message_type)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Message Detail */}
        <Card>
          <CardHeader>
            <CardTitle>Message Details</CardTitle>
          </CardHeader>
          <CardContent>
            {selectedMessage ? (
              <div className="space-y-4">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-semibold text-lg">{selectedMessage.subject}</h3>
                    <p className="text-gray-600">{selectedMessage.name}</p>
                    <p className="text-sm text-gray-500">{selectedMessage.email}</p>
                    {selectedMessage.phone && <p className="text-sm text-gray-500">{selectedMessage.phone}</p>}
                  </div>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={() => handleStatusChange(selectedMessage.id, 'archived')}>
                      <Archive className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDelete(selectedMessage.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-700 whitespace-pre-wrap">{selectedMessage.message}</p>
                </div>
                
                <p className="text-xs text-gray-400">
                  Received: {new Date(selectedMessage.created_at).toLocaleString()}
                </p>

                {selectedMessage.admin_reply && (
                  <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                    <p className="text-sm font-medium text-green-700 mb-2">Your Reply:</p>
                    <p className="text-gray-700">{selectedMessage.admin_reply}</p>
                    <p className="text-xs text-gray-400 mt-2">
                      Replied: {new Date(selectedMessage.replied_at).toLocaleString()}
                    </p>
                  </div>
                )}

                {selectedMessage.status !== 'replied' && (
                  <div className="space-y-2">
                    <Textarea
                      placeholder="Write your reply..."
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      rows={4}
                    />
                    <Button onClick={() => handleReply(selectedMessage.id)} className="bg-saffron-500 hover:bg-saffron-600">
                      <Reply className="w-4 h-4 mr-2" />
                      Send Reply
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center text-gray-500 py-12">
                <Mail className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>Select a message to view details</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminMessages;
